// Authored content for the build-spec lesson — CD02's V2401, CD03's V3401 and
// CD04's V4401, "Turning your idea into a build spec with Claude".
//
// WHY THIS IS AUTHORED RATHER THAN GENERATED
//
// This lesson is the second half of a pair. Module 1's opening lesson (V2101 /
// V3101 / V4101) has the student choose an app and talk the idea through; this one
// turns that idea into a build spec. For it to grade the GAPS — the whole point — the
// rubric has to be a fixed set of slots that can be checked one by one against
// what Module 1 produced. A per-user generated rubric can't be: it comes back
// worded differently for every student, so there is nothing stable to mark as
// "already settled in Module 1", and in practice it re-asked for the app idea and
// the first two features the student had chosen weeks earlier.
//
// So the six slots below are pinned, and the hub overrides `definition.rubric`,
// `exercisePrompt` and `hint` on read — the same precedent as the authored canvas
// (pages/claudeDevCourse/authoredCanvases.js) and the authored Web Lab exercises
// (starterTemplate/authoredLessons.js). Overriding on read means no cache purge
// and no DEF_VERSION bump: CD02's students keep their cached prose.
//
// `lessonMarkdown` is deliberately NOT overridden. It teaches what a build spec is,
// which is the same lesson either way, and its pregenerated ElevenLabs narration is
// keyed to that text — replacing it would desync the voice track from the page.
//
// SLOT IDS ARE A CONTRACT with the backend: the same six ids are produced by
// BUILD_SPEC_SLOTS in backend/src/services/ClaudeCourseService.js, whose extractor
// decides which are already covered. Change one, change both.

/** The lessons this file drives. CD01 has no equivalent — its Module 4 doesn't
 *  build the student's own app — so only the project courses appear.
 *
 *  CD04's V4401 is the same lesson moved: it opens that course's Claude Code module
 *  rather than a Web Lab build module, because the project folder it hands out at
 *  100% is what the student then opens in Claude Code. Missing from this list, the
 *  lesson still renders and still grades — against a GENERATED rubric that re-asks
 *  for the app idea Module 1 already settled, which is the exact failure this file
 *  was written to fix, and it fails silently. */
export const BUILD_SPEC_LESSON_IDS = ["V2401", "V3401", "V4401"];

export const isBuildSpecLesson = (lessonID) => BUILD_SPEC_LESSON_IDS.includes(lessonID);

/** Ordered slot ids. The rubric renders in this order, so it reads as a spec
 *  being filled in: what it does → what it stores → where you see it → what
 *  happens → what's out of scope → say it back. */
export const BUILD_SPEC_SLOTS = ["features", "data", "screens", "actions", "scope", "confirmed"];

// Copy per slot, per locale, with two different audiences. `criterion` is what the
// judge grades and what the rubric panel lists — it is the ONLY one of the two the
// student reads. `gap` is the short imperative handed to Claude in the system prompt
// (buildSpecChatInstructions) to say which slots are still open.
//
// The exercise used to list the `gap` lines as bullets too, which put the same six
// items on screen twice in two voices — and in different lengths, since the exercise
// showed only the open ones while the rubric always shows all six. The rubric owns
// the checklist now: it is the panel that stays on screen during the chat and the only
// one that can show state (carried tick, pass, fail + reason). Don't put the list back
// in the exercise.
//
// WORD THE FIRST FIVE AS "the conversation settles X", NEVER "the build spec says X".
// This lesson is graded on a transcript — there is no document to inspect — and the
// document phrasing made the judge demand a written spec for every line: it failed
// "names the screens" while its own reason said the screens had been discussed and
// named, because they had not been written out formally. That is the LAST slot's job
// and only the last slot's job. Phrased this way, the first five pass on decisions
// made and `confirmed` remains the one that asks for the spec in one piece.
const SLOT_COPY = {
    en: {
        features: {
            criterion:
                "The student and Claude have settled at least three specific things a user can do in the app.",
            gap: "the three things a user can actually do",
        },
        data: {
            criterion:
                "The conversation establishes what information the app stores or remembers.",
            gap: "what the app needs to remember",
        },
        screens: {
            criterion:
                "The conversation names the screens the app has and what each one is for.",
            gap: "the screens people will see",
        },
        actions: {
            criterion:
                "The conversation describes the main flow step by step: the user does something, then the app does something.",
            gap: "what happens step by step when someone uses it",
        },
        // "Version 1" is developer shorthand this course has not taught, on a page
        // that promises plain language — so the slot says what it means instead of
        // naming it. Both lines carry the definition: the criterion because that is
        // what the student reads in the rubric, the gap because that is what Claude
        // is told to ask about.
        scope: {
            criterion:
                "The student has said which features they are leaving for later, so the first version stays small enough to finish.",
            gap: "what you're leaving for later, so the first version is small enough to actually finish",
        },
        confirmed: {
            criterion:
                "Claude has written the complete build spec out in one message, and the student confirmed it is right.",
            gap: "the finished spec, written out in one place",
        },
    },
    es: {
        features: {
            criterion:
                "El estudiante y Claude ya definieron al menos tres cosas concretas que un usuario puede hacer en la app.",
            gap: "las tres cosas que un usuario puede hacer",
        },
        data: {
            criterion:
                "La conversación deja claro qué información guarda o recuerda la app.",
            gap: "qué necesita recordar la app",
        },
        screens: {
            criterion:
                "La conversación nombra las pantallas que tiene la app y para qué sirve cada una.",
            gap: "las pantallas que verá la gente",
        },
        actions: {
            criterion:
                "La conversación describe el flujo principal paso a paso: el usuario hace algo y después la app hace algo.",
            gap: "qué pasa paso a paso cuando alguien la usa",
        },
        scope: {
            criterion:
                "El estudiante dijo qué funciones deja para más adelante, para que la primera versión sea lo bastante pequeña como para terminarla.",
            gap: "qué dejas para más adelante, para que la primera versión se pueda terminar de verdad",
        },
        confirmed: {
            criterion:
                "Claude escribió el plan de construcción completo en un solo mensaje y el estudiante confirmó que está bien.",
            gap: "el plan terminado, escrito en un solo lugar",
        },
    },
};

// The exercise prose. It says what to DO and points at the rubric — it does NOT
// restate the rubric's lines; see the SLOT_COPY note above. Two versions of the
// opening: one for a student whose Module 1 carried something over, one for a student
// starting from scratch here (they skipped Module 1, or the extraction found nothing).
const EXERCISE_COPY = {
    en: {
        carriedLead:
            "You already settled part of this in Module 1 — those lines are ticked in the rubric, so don't repeat them. Work through what's left with Claude, one line at a time.",
        freshLead:
            "Talk your app idea through with Claude until every line of the rubric is settled — one at a time, not all at once.",
        allCovered:
            "Everything is settled except the last line — ask Claude to write the whole spec out in one message, read it, and confirm it's right.",
        closing:
            "When it's all there, ask Claude to write the whole spec out in one message, then check it says what you meant.",
        hint:
            "A good move: paste what you already have, then ask **\"what's missing from this build spec?\"** — Claude will name the gaps, and you can answer them one at a time. Keep that first version small: it's the one you'll actually finish, and everything else can come after it. \"Users can add an item, see the list, and delete one\" is a finishable spec; \"users can do everything\" is not.",
        carriedNote:
            "Carried over from Module 1",
    },
    es: {
        carriedLead:
            "Ya resolviste parte de esto en el Módulo 1 — esas líneas están marcadas en la rúbrica, así que no hace falta repetirlas. Trabaja lo que falta con Claude, una línea a la vez.",
        freshLead:
            "Habla tu idea de app con Claude hasta que quede resuelta cada línea de la rúbrica — de a una, no todas de golpe.",
        allCovered:
            "Ya está todo resuelto menos la última línea — pídele a Claude que escriba el plan completo en un solo mensaje, léelo y confirma que está bien.",
        closing:
            "Cuando ya esté todo, pídele a Claude que escriba el plan completo en un solo mensaje y revisa que diga lo que querías decir.",
        hint:
            "Un buen movimiento: pega lo que ya tienes y pregunta **\"¿qué le falta a este plan de construcción?\"** — Claude te dirá los huecos y los puedes ir respondiendo de a uno. Mantén pequeña esa primera versión: es la que de verdad vas a terminar, y todo lo demás puede venir después. \"El usuario puede agregar un elemento, ver la lista y borrar uno\" es un plan que se termina; \"el usuario puede hacer de todo\" no.",
        carriedNote:
            "Viene del Módulo 1",
    },
};

const copyFor = (locale) => (locale === "es" ? "es" : "en");

/**
 * The lesson's rubric, one entry per slot, in slot order.
 *
 * @param {string} locale
 * @param {object} [covered]  { [slotId]: boolean } from the backend extractor
 * @returns {Array<{ id, criterion, carried }>}  `carried` = already settled in Module 1
 */
export const buildSpecRubric = (locale, covered = {}) => {
    const copy = SLOT_COPY[copyFor(locale)];
    return BUILD_SPEC_SLOTS.map((id) => ({
        id,
        criterion: copy[id].criterion,
        carried: !!covered[id],
    }));
};

/**
 * The exercise prompt and hint. Two sentences pointing at the rubric — the rubric
 * panel is the checklist, this only says what to do with it.
 *
 * @param {string} locale
 * @param {object} [covered]  { [slotId]: boolean } from the backend extractor
 * @returns {{ exercisePrompt: string, hint: string }}
 */
export const buildSpecExercise = (locale, covered = {}) => {
    const copy = EXERCISE_COPY[copyFor(locale)];

    const openSlots = BUILD_SPEC_SLOTS.filter((id) => !covered[id]);
    const anyCarried = BUILD_SPEC_SLOTS.some((id) => covered[id]);

    // "confirmed" is always open (Module 1 can't satisfy it), so a student with
    // only that one left has effectively finished the spec itself.
    const onlyConfirmLeft = openSlots.length === 1 && openSlots[0] === "confirmed";

    const exercisePrompt = onlyConfirmLeft
        ? copy.allCovered
        : [anyCarried ? copy.carriedLead : copy.freshLead, "", copy.closing].join("\n");

    return { exercisePrompt, hint: copy.hint };
};

/**
 * Extra instructions appended to the lesson's own system prompt, so the chat
 * opens on the gaps instead of asking the student to describe their app again.
 * The student's actual Module 1 decisions are added server-side by
 * buildSpecBrief — this only says what to DO with them.
 */
export const buildSpecChatInstructions = (locale, covered = {}) => {
    const slotCopy = SLOT_COPY[copyFor(locale)];
    const openSlots = BUILD_SPEC_SLOTS.filter((id) => !covered[id] && id !== "confirmed");
    const carried = BUILD_SPEC_SLOTS.filter((id) => covered[id]);

    const lines = [
        "",
        "",
        "THIS LESSON TURNS THE STUDENT'S IDEA INTO A BUILD SPEC.",
    ];

    if (carried.length > 0) {
        lines.push(
            `They already settled these in an earlier lesson: ${carried.join(", ")}. Treat those as DECIDED — restate them if useful, but never ask the student to choose them again.`
        );
    }

    if (openSlots.length > 0) {
        lines.push(
            `Still open, and what this conversation is for: ${openSlots
                .map((id) => `${id} (${slotCopy[id].gap})`)
                .join("; ")}.`,
            "Work through the open items one at a time — ask about ONE, wait for their answer, then move to the next. Do not dump every question at once.",
            "When they have all been answered, write the complete build spec out in one place and ask the student to confirm it."
        );
    } else {
        lines.push(
            "Everything is decided. Write the complete build spec out in one place and ask the student to confirm it says what they meant."
        );
    }

    lines.push(
        "Keep the first version small and finishable — the smallest thing that is still useful to a real person. Push back gently on scope they cannot build in a week.",
        "This student is a beginner and the course promises plain language. Do not use developer shorthand like \"version 1\", \"MVP\", \"scope\" or \"schema\" without saying what it means in the same breath."
    );

    return lines.join("\n");
};

/** The "Carried over from Module 1" tag shown on pre-credited rubric lines. */
export const buildSpecCarriedLabel = (locale) => EXERCISE_COPY[copyFor(locale)].carriedNote;
